/**
 * useConversations Hook
 * API hooks for conversation management operations
 *
 * Refactored: Phase 1 - Factory Pattern Migration
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 *
 * Previous implementation: 440 lines with manual useQuery/useMutation setup
 * Current implementation: Uses standardized factory pattern
 * Improvements:
 * - Automatic logging via factory logContext
 * - Consistent error handling
 * - Reduced boilerplate code
 * - Type-safe parameters and return types
 */

import { useQueryClient } from '@tanstack/react-query';
import type {
  ConversationSummary,
  ConversationDetails,
  MessageResponse,
  UpdateConversationRequest,
} from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook, type QueryHookOptions } from './factories';

// Query Keys
export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (filters: string) => [...conversationKeys.lists(), { filters }] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
  messages: (id: string) => [...conversationKeys.all, 'messages', id] as const,
  search: (query: string) => [...conversationKeys.all, 'search', query] as const,
};

// --- Query Hooks ---

/**
 * Internal factory hook for getting conversations
 */
const useGetConversationsFactory = createQueryHook<
  ConversationSummary[],
  Record<string, never>
>({
  queryKey: () => conversationKeys.lists(),
  queryFn: (apiClient) =>
    apiClient
      .getConversationsApiConversationsGet()
      .then((response) => response.data.items),
  logContext: 'GET /api/conversations',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data) => {
    log.api.response('GET', '/api/conversations', 200, { count: data.length });
  },
});

/**
 * Hook to get all conversations for the current user
 */
export function useGetConversations() {
  return useGetConversationsFactory({});
}

/**
 * Internal factory hook for getting conversation details
 */
const useGetConversationFactory = createQueryHook<
  ConversationDetails,
  { conversationId: string | null }
>({
  queryKey: (params) => conversationKeys.detail(params.conversationId ?? ''),
  queryFn: async (apiClient, params) => {
    if (!params.conversationId) {
      throw new Error('No conversation ID provided');
    }
    const response = await apiClient.getConversationApiConversationsConversationIdGet(
      params.conversationId
    );
    return response.data;
  },
  logContext: 'GET /api/conversations/:id',
  staleTime: 10 * 1000, // 10 seconds
  onSuccess: (data) => {
    log.api.response('GET', `/api/conversations/${data.id}`, 200, {
      messageCount: data.messages?.length ?? 0,
      title: data.title,
    });
  },
});

/**
 * Hook to get conversation details including messages
 * @param conversationId - The ID of the conversation to fetch
 * @param options - Query options (enabled, queryOptions, etc.)
 */
export function useGetConversation(
  conversationId: string | null,
  options?: QueryHookOptions<ConversationDetails>
) {
  return useGetConversationFactory({ conversationId }, options);
}

/**
 * Internal factory hook for getting conversation messages
 */
const useGetConversationMessagesFactory = createQueryHook<
  MessageResponse[],
  { conversationId: string | null; limit?: number; offset?: number }
>({
  queryKey: (params) => conversationKeys.messages(params.conversationId ?? ''),
  queryFn: async (apiClient, params) => {
    if (!params.conversationId) {
      throw new Error('No conversation ID provided');
    }
    const response =
      await apiClient.getConversationMessagesApiConversationsConversationIdMessagesGet(
        params.conversationId,
        { page: 1, page_size: params.limit ?? 50 }
      );
    return response.data.items;
  },
  logContext: 'GET /api/conversations/:id/messages',
  staleTime: 5 * 1000, // 5 seconds
  onSuccess: (data) => {
    log.api.response('GET', '/api/conversations/:id/messages', 200, {
      count: data.length,
    });
  },
});

/**
 * Hook to get messages for a conversation
 * @param conversationId - The ID of the conversation
 * @param paginationOptions - Pagination options (limit, offset)
 * @param queryOptions - Query options (enabled, queryOptions, etc.)
 */
export function useGetConversationMessages(
  conversationId: string | null,
  paginationOptions?: { limit?: number; offset?: number },
  queryOptions?: QueryHookOptions<MessageResponse[]>
) {
  return useGetConversationMessagesFactory(
    {
      conversationId,
      ...paginationOptions,
    },
    queryOptions
  );
}

// --- Mutation Hooks ---

/**
 * Hook to create a new conversation
 */
export const useCreateConversation = createMutationHook<
  ConversationDetails,
  string | undefined
>({
  mutationFn: (apiClient, title) => {
    const request = { title: title ?? 'Neuer Chat' };
    return apiClient
      .createConversationApiConversationsPost(request)
      .then((response) => response.data);
  },
  invalidateQueries: [conversationKeys.lists()],
  logContext: 'POST /api/conversations',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('conversation_created', 'useConversations', {
      conversationId: data.id,
      title: data.title,
    });
    log.api.response('POST', '/api/conversations', 200, {
      id: data.id,
      title: data.title,
    });
  },
});

/**
 * Hook to update a conversation
 * Returns a mutation that updates conversation details and cache
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return createMutationHook<
    ConversationDetails,
    { conversationId: string; updates: UpdateConversationRequest }
  >({
    mutationFn: (apiClient, params) =>
      apiClient
        .updateConversationApiConversationsConversationIdPut(
          params.conversationId,
          params.updates
        )
        .then((response) => response.data),
    invalidateQueries: [conversationKeys.lists()],
    logContext: 'PUT /api/conversations/:id',
    logMethod: 'PUT',
    onSuccess: (data, variables) => {
      // Update conversation details in cache
      queryClient.setQueryData<ConversationDetails>(
        conversationKeys.detail(variables.conversationId),
        (old) => (old ? { ...old, ...data } : data)
      );

      log.user.action('conversation_updated', 'useConversations', {
        conversationId: variables.conversationId,
        title: data.title,
      });
      log.api.response('PUT', `/api/conversations/${variables.conversationId}`, 200, {
        title: data.title,
      });
    },
  })();
}

/**
 * Hook to delete a conversation
 * Returns a mutation that deletes conversation and clears related cache
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return createMutationHook<unknown, string>({
    mutationFn: (apiClient, conversationId) =>
      apiClient
        .deleteConversationApiConversationsConversationIdDelete(conversationId)
        .then((response) => response.data),
    invalidateQueries: [conversationKeys.lists()],
    logContext: 'DELETE /api/conversations/:id',
    logMethod: 'DELETE',
    onSuccess: (_data, conversationId) => {
      // Remove conversation details and messages from cache
      void queryClient.removeQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
      void queryClient.removeQueries({
        queryKey: conversationKeys.messages(conversationId),
      });

      log.user.action('conversation_deleted', 'useConversations', {
        conversationId,
      });
      log.api.response('DELETE', `/api/conversations/${conversationId}`, 200, {});
    },
  })();
}

/**
 * Hook to clear conversation messages
 * Returns a mutation that clears messages and updates cache
 */
export function useClearConversationMessages() {
  const queryClient = useQueryClient();

  return createMutationHook<unknown, string>({
    mutationFn: (apiClient, conversationId) =>
      apiClient
        .clearConversationMessagesApiConversationsConversationIdMessagesDelete(
          conversationId
        )
        .then((response) => response.data),
    logContext: 'DELETE /api/conversations/:id/messages',
    logMethod: 'DELETE',
    onSuccess: (_data, conversationId) => {
      // Clear messages cache
      queryClient.setQueryData(conversationKeys.messages(conversationId), []);

      // Update conversation details to reset message count
      queryClient.setQueryData<ConversationDetails>(
        conversationKeys.detail(conversationId),
        (old) => (old ? { ...old, messages: [], message_count: 0 } : old)
      );

      log.user.action('conversation_cleared', 'useConversations', {
        conversationId,
      });
      log.api.response(
        'DELETE',
        `/api/conversations/${conversationId}/messages`,
        200,
        {}
      );
    },
  })();
}

/**
 * Hook to generate conversation title
 * Returns a mutation that generates title and updates cache
 */
export function useGenerateConversationTitle() {
  const queryClient = useQueryClient();

  return createMutationHook<{ title: string }, string>({
    mutationFn: async (apiClient, conversationId) => {
      const response =
        await apiClient.generateConversationTitleApiConversationsConversationIdGenerateTitleGet(
          conversationId
        );
      return response.data as { title: string };
    },
    invalidateQueries: [conversationKeys.lists()],
    logContext: 'GET /api/conversations/:id/generate-title',
    logMethod: 'GET',
    onSuccess: (data, conversationId) => {
      // Update conversation details in cache
      queryClient.setQueryData<ConversationDetails>(
        conversationKeys.detail(conversationId),
        (old) => (old ? { ...old, title: data.title } : old)
      );

      log.user.action('title_generated', 'useConversations', {
        conversationId,
        title: data.title,
      });
      log.api.response(
        'GET',
        `/api/conversations/${conversationId}/generate-title`,
        200,
        {
          title: data.title,
        }
      );
    },
  })();
}

/**
 * Hook to search conversations
 * Note: This is a mutation hook because searches are dynamic and shouldn't be cached
 */
export const useSearchConversations = createMutationHook<
  ConversationSummary[],
  string
>({
  mutationFn: (apiClient, query) => {
    if (!query.trim()) {
      return Promise.resolve([]);
    }
    return apiClient
      .searchConversationsApiConversationsSearchGet({ q: query, limit: 10 })
      .then((response) => response.data);
  },
  logContext: 'GET /api/conversations/search/',
  logMethod: 'GET',
  onSuccess: (data) => {
    log.api.response('GET', '/api/conversations/search/', 200, {
      count: data.length,
    });
  },
});
